import React from 'react';
import { notification } from '../../../utility/notification/notification.service';
import {
  basePatchQuery,
  basePostQuery,
} from '../../../utility/sb-api/sb-api.service';
import { getLocaleHierarchy } from '../../../utility/util/util.service';
import LocalizedNameField from '../form/fields/LocalizedNameField';
import Collapse from '../collapsables/collapse/collapse.component';
import LocalizedDescriptionField from '../form/fields/LocalizedDescriptionField';
import TextEditorField from '../form/fields/TextEditorField';
import LocalizedTitleField from '../form/fields/LocalizedTitleField';
import ItemServiceTimes from '../order/ItemServiceTimes';
import * as Icon from 'react-feather';
import { FormattedMessage } from 'react-intl';
import LabeledToggle from '../form/LabeledToggle';
import ClassesField from '../form/fields/ClassesField';
import FormField from '../form/fields/FormField';
import { Col, Row } from 'reactstrap';
import FormattedNumberField from '../form/fields/FormattedNumberField';

export const closeModalSafely = (isSubmitting, closeModal) => {
  if (isSubmitting) {
    notification('warning', 'isSubmittingCancelWarning');
  } else {
    closeModal();
  }
};

export const handleSubmit = (
  path,
  editSuccessMessageId,
  editErrorMessageId,
  createSuccessMessageId,
  createErrorMessageId,
  event,
  errors,
  values,
  targetData,
  isEditMode,
  setIsSubmitting,
  setTargetData,
  closeModal,
  onSubmit,
) => {
  if (!errors?.length > 0) {
    setIsSubmitting(true);

    const data = {
      ...targetData,
      ...values,
    };

    return (isEditMode
      ? basePatchQuery(
          `${path}/${targetData.id}`,
          data,
          null,
          editSuccessMessageId,
          editErrorMessageId,
        )
      : basePostQuery(
          path,
          data,
          null,
          createSuccessMessageId,
          createErrorMessageId,
        )
    )
      .then((data) => {
        closeModal();

        // since the modal close animation takes some time, we need to wait a little bit
        setTimeout(() => {
          onSubmit(data);
          setIsSubmitting(false);
          setTargetData({});
          closeModal();
        }, 500);

        return data;
      })
      .catch((error) => {
        console.error(error?.response?.data?.error);
        setIsSubmitting(false);
      });
  } else {
    console.error('Got validation errors in ' + errors.join(', '));
  }
};

export const getTranslationsCollapsable = (
  targetData,
  isTranslationsOpen,
  setIsTranslationsOpen,
  showDescriptionField = false,
  showNameField = true,
  intlLocale,
  showTextEditorDescription,
  showTitle,
  setTargetItemData,
  hideBottomDivider,
) => {
  const localeHierarchy = getLocaleHierarchy(intlLocale);
  const additionalLocales = localeHierarchy.slice(1); // slice the first one

  return (
    <Collapse
      hideBottomDivider={hideBottomDivider}
      isOpen={isTranslationsOpen}
      setIsOpen={setIsTranslationsOpen}
      titleId="itemTranslationsTitle"
    >
      {additionalLocales.map((locale, index) => {
        if (locale !== intlLocale.toUpperCase()) {
          return (
            <div key={index}>
              {showNameField && (
                <LocalizedNameField object={targetData} locale={locale} />
              )}
              {showTitle && (
                <LocalizedTitleField object={targetData} locale={locale} />
              )}
              {showDescriptionField && !showTextEditorDescription && (
                <>
                  <LocalizedDescriptionField
                    object={targetData}
                    locale={locale}
                  />
                </>
              )}
              {showTextEditorDescription && (
                <>
                  <TextEditorField
                    setter={setTargetItemData}
                    object={targetData}
                    locale={locale}
                  />
                </>
              )}
            </div>
          );
        }
      })}
    </Collapse>
  );
};

export const getServiceTimesCollapsable = (
  targetData,
  isTranslationsOpen,
  setIsTranslationsOpen,
  setServiceTimesLocalState,
) => {
  const convertDoubleArrayOfObjectToArray = (arrays) => {
    return arrays.flatMap((innerArray) =>
      innerArray.map(({ startAt, endAt }) => ({ startAt, endAt })),
    );
  };

  const sortEventsByStartDate = (events) => {
    events.sort(function(a, b) {
      return new Date(a.startAt) - new Date(b.startAt);
    });
    return events;
  };

  const onAddClick = (obj) => {
    const convertedArray = convertDoubleArrayOfObjectToArray(obj);
    const payloadArray = sortEventsByStartDate(convertedArray);
    setServiceTimesLocalState(payloadArray);
  };
  return (
    <Collapse
      isOpen={isTranslationsOpen}
      setIsOpen={setIsTranslationsOpen}
      titleId="itemAvailableAtTitle"
    >
      <ItemServiceTimes onAddClick={onAddClick} />
    </Collapse>
  );
};

export const getOrderConditionsCollapsable = (
  targetOrderConditions,
  setTargetOrderConditions,
  isOrderConditionsOpen,
  setIsOrderConditionsOpen,
  userLocale,
  isSubmitting,
  intl,
  isAddOrderConditionOpen,
  setIsAddOrderConditionOpen,
  newOrderCondition,
  setNewOrderCondition,
  title,
  disableFieldIfItemData,
  selectedOrderCondition,
  setSelectedOrderCondition,
  hideBottomDivider,
) => {
  //fns related to service times within orderConditions
  const convertDoubleArrayOfObjectToArray = (arrays) => {
    return arrays.flatMap((innerArray) =>
      innerArray.map(({ startAt, endAt }) => ({ startAt, endAt })),
    );
  };

  const sortEventsByStartDate = (events) => {
    events.sort(function(a, b) {
      return new Date(a.startAt) - new Date(b.startAt);
    });
    return events;
  };

  const onAddClick = (obj, setter) => {
    const convertedArray = convertDoubleArrayOfObjectToArray(obj);
    const payloadArray = sortEventsByStartDate(convertedArray);
    setter(payloadArray);
  };

  return (
    <Collapse
      hideBottomDivider={hideBottomDivider}
      isOpen={isOrderConditionsOpen}
      setIsOpen={setIsOrderConditionsOpen}
      titleId={title || 'orderConditionsTitle'}
    >
      <div className="pl-50">
        {targetOrderConditions?.length > 0 &&
          targetOrderConditions?.map((orderCondition, index) => (
            <div key={index}>
              <Collapse
                isOpen={selectedOrderCondition === orderCondition?.id}
                setIsOpen={() =>
                  selectedOrderCondition !== orderCondition?.id
                    ? setSelectedOrderCondition(orderCondition?.id)
                    : setSelectedOrderCondition(null)
                }
                titleId={orderCondition?.name || 'name'}
                showDeleteButton
                removeOrderCondition={() =>
                  setTargetOrderConditions((prevState) => {
                    return prevState.filter(
                      (orderCondition, index2) => index !== index2,
                    );
                  })
                }
              >
                <div className="mt-2">
                  <LabeledToggle
                    disabled={isSubmitting}
                    checked={orderCondition?.isActive}
                    onClick={() =>
                      setTargetOrderConditions((prevState) => {
                        return prevState.map((prevOrderCondition, index2) => {
                          if (index === index2) {
                            return {
                              ...prevOrderCondition,
                              isActive: !prevOrderCondition?.isActive,
                            };
                          } else {
                            return prevOrderCondition;
                          }
                        });
                      })
                    }
                    wrapperClassName="d-flex justify-content-between mt-2 mb-2"
                    headingMessageId="orderConditionEnabledLabel"
                    headingClassName="d-block dark"
                  />
                  <Row>
                    <Col xs={6} className="pr-50">
                      <FormField
                        object={orderCondition}
                        propertyName="name"
                        messageId="name"
                        onChange={(e) => {
                          setTargetOrderConditions((prevState) => {
                            return prevState.map(
                              (prevOrderCondition, index2) => {
                                if (index === index2) {
                                  return {
                                    ...prevOrderCondition,
                                    name: e.target.value,
                                  };
                                } else {
                                  return prevOrderCondition;
                                }
                              },
                            );
                          });
                        }}
                        required
                      />
                    </Col>
                    <Col xs={6} className="pl-50">
                      <FormField
                        object={orderCondition}
                        propertyName="priority"
                        type="number"
                        messageId="priorityLabel"
                        onChange={(e) => {
                          setTargetOrderConditions((prevState) => {
                            return prevState.map(
                              (prevOrderCondition, index2) => {
                                if (index === index2) {
                                  return {
                                    ...prevOrderCondition,
                                    priority: parseInt(e.target.value),
                                  };
                                } else {
                                  return prevOrderCondition;
                                }
                              },
                            );
                          });
                        }}
                        required
                        validate={{ number: true }}
                        valueParser={(value) => {
                          const parsedValue = parseInt(value);
                          return parsedValue >= 0 ? parsedValue : {}; // {} is definitely not a number
                        }}
                      />
                    </Col>
                  </Row>
                  <ClassesField
                    object={orderCondition}
                    locale={userLocale}
                    setter={(newOrderCondition) => {
                      setTargetOrderConditions((prevState) => {
                        return prevState.map((prevOrderCondition, index2) => {
                          if (index === index2) {
                            return newOrderCondition;
                          } else {
                            return prevOrderCondition;
                          }
                        });
                      });
                    }}
                    isDisabled={isSubmitting}
                  />
                  <Row>
                    {/*<Col xs={6} className="pr-50">
                      <FormattedNumberField
                        propName="minTotal"
                        labelMessageId="minTotalLabel"
                        object={orderCondition}
                        disabled={disableFieldIfItemData}
                        onChange={(value) =>
                          setTargetOrderConditions((prevState) => {
                            return prevState.map(
                              (prevOrderCondition, index2) => {
                                if (index === index2) {
                                  return {
                                    ...prevOrderCondition,
                                    minTotal: value,
                                  };
                                } else {
                                  return prevOrderCondition;
                                }
                              },
                            );
                          })
                        }
                        allowNullValue
                      />
                    </Col>
                    <Col xs={6} className="pl-50">
                      <FormattedNumberField
                        propName="maxTotal"
                        labelMessageId="maxTotalLabel"
                        object={orderCondition}
                        onChange={(value) => {
                          setTargetOrderConditions((prevState) => {
                            return prevState.map(
                              (prevOrderCondition, index2) => {
                                if (index === index2) {
                                  return {
                                    ...prevOrderCondition,
                                    maxTotal: value,
                                  };
                                } else {
                                  return prevOrderCondition;
                                }
                              },
                            );
                          });
                        }}
                        allowNullValue
                      />
                    </Col>*/}
                    <Col xs={6} className="pr-50">
                      <FormField
                        object={orderCondition}
                        propertyName="minQty"
                        messageId="itemMinQtyLabel"
                        disabled={disableFieldIfItemData}
                        value={orderCondition?.minQty?.toString()}
                        onChange={(e) => {
                          setTargetOrderConditions((prevState) => {
                            return prevState.map(
                              (prevOrderCondition, index2) => {
                                if (index === index2) {
                                  return {
                                    ...prevOrderCondition,
                                    minQty: parseInt(e.target.value),
                                  };
                                } else {
                                  return prevOrderCondition;
                                }
                              },
                            );
                          });
                        }}
                      />
                    </Col>
                    <Col xs={6} className="pl-50">
                      <FormField
                        object={orderCondition}
                        propertyName="maxQty"
                        messageId="itemMaxQtyLabel"
                        onChange={(e) => {
                          setTargetOrderConditions((prevState) => {
                            return prevState.map(
                              (prevOrderCondition, index2) => {
                                if (index === index2) {
                                  return {
                                    ...prevOrderCondition,
                                    maxQty:
                                      e.target.value === ''
                                        ? null
                                        : parseInt(e.target.value),
                                  };
                                } else {
                                  return prevOrderCondition;
                                }
                              },
                            );
                          });
                        }}
                      />
                    </Col>
                  </Row>
                  <ItemServiceTimes
                    onAddClick={(serviceTimes) => {
                      onAddClick(serviceTimes, (serviceTimesArray) =>
                        setTargetOrderConditions((prevState) => {
                          return prevState.map((prevOrderCondition, index2) => {
                            if (index === index2) {
                              return {
                                ...prevOrderCondition,
                                serviceTimes: serviceTimesArray,
                              };
                            } else {
                              return prevOrderCondition;
                            }
                          });
                        }),
                      );
                    }}
                    orderConditionAvilableAt={orderCondition?.serviceTimes}
                  />
                </div>
              </Collapse>
            </div>
          ))}
        <div className="pb-1">
          <div className="d-flex justify-content-between align-items-center my-2">
            <span className="font-medium-1 font-weight-bolder">
              <FormattedMessage id="addOrderConditionText" />
            </span>
            <span>
              {isAddOrderConditionOpen ? (
                <Icon.MinusSquare
                  size={22}
                  className="primary"
                  onClick={() => setIsAddOrderConditionOpen(false)}
                />
              ) : (
                <Icon.PlusSquare
                  size={22}
                  data-testid="addOrderCondition"
                  className="primary"
                  onClick={() => setIsAddOrderConditionOpen(true)}
                />
              )}
            </span>
          </div>
          {isAddOrderConditionOpen && (
            <>
              <LabeledToggle
                disabled={isSubmitting}
                checked={newOrderCondition?.isActive}
                onClick={() =>
                  setNewOrderCondition((prevState) => {
                    return {
                      ...prevState,
                      isActive: !prevState?.isActive,
                    };
                  })
                }
                wrapperClassName="d-flex justify-content-between mt-2 mb-2"
                headingMessageId="orderConditionEnabledLabel"
                headingClassName="d-block dark"
              />
              <Row>
                <Col xs={6} className="pr-50">
                  <FormField
                    object={newOrderCondition}
                    propertyName="name"
                    messageId="name"
                    onChange={(e) => {
                      setNewOrderCondition((prevState) => {
                        return {
                          ...prevState,
                          name: e.target.value,
                        };
                      });
                    }}
                    required
                  />
                </Col>
                <Col xs={6} className="pl-50">
                  <FormField
                    object={newOrderCondition}
                    propertyName="priority"
                    type="number"
                    messageId="priorityLabel"
                    onChange={(e) => {
                      setNewOrderCondition((prevState) => {
                        return {
                          ...prevState,
                          priority: parseInt(e.target.value),
                        };
                      });
                    }}
                    required
                  />
                </Col>
              </Row>
              <ClassesField
                object={newOrderCondition}
                locale={userLocale}
                setter={setNewOrderCondition}
                isDisabled={isSubmitting}
              />
              <Row>
                {/*<Col xs={6} className="pr-50">
                  <FormattedNumberField
                    propName="minTotal"
                    labelMessageId="minTotalLabel"
                    object={newOrderCondition}
                    disabled={disableFieldIfItemData}
                    onChange={(value) =>
                      setNewOrderCondition((prevState) => {
                        return {
                          ...prevState,
                          minTotal: value,
                        };
                      })
                    }
                    required
                  />
                </Col>
                <Col xs={6} className="pl-50">
                  <FormattedNumberField
                    propName="maxTotal"
                    labelMessageId="maxTotalLabel"
                    object={newOrderCondition}
                    onChange={(value) => {
                      setNewOrderCondition((prevState) => {
                        return {
                          ...prevState,
                          maxTotal: value,
                        };
                      });
                    }}
                    allowNullValue
                  />
                </Col>*/}
                <Col xs={6} className="pr-50">
                  <FormField
                    disabled={disableFieldIfItemData}
                    object={newOrderCondition}
                    propertyName="minQty"
                    messageId="itemMinQtyLabel"
                    value={newOrderCondition?.minQty?.toString()}
                    onChange={(e) => {
                      setNewOrderCondition((prevState) => {
                        return {
                          ...prevState,
                          minQty: parseInt(e.target.value),
                        };
                      });
                    }}
                  />
                </Col>
                <Col xs={6} className="pl-50">
                  <FormField
                    object={newOrderCondition}
                    propertyName="maxQty"
                    messageId="itemMaxQtyLabel"
                    onChange={(e) => {
                      setNewOrderCondition((prevState) => {
                        return {
                          ...prevState,
                          maxQty: parseInt(e.target.value),
                        };
                      });
                    }}
                  />
                </Col>
              </Row>
              <ItemServiceTimes
                onAddClick={(serviceTimes) => {
                  onAddClick(serviceTimes, (serviceTimesArray) =>
                    setNewOrderCondition((prevState) => {
                      return {
                        ...prevState,
                        serviceTimes: serviceTimesArray,
                      };
                    }),
                  );
                }}
                orderConditionAvilableAt={newOrderCondition?.serviceTimes}
              />
            </>
          )}
        </div>
      </div>
    </Collapse>
  );
};
